import React, { memo, useCallback, useState } from 'react';

import { Flex } from '@chakra-ui/layout';
import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/modal';

import { Button, Icon, MaskInput, Unform } from 'components';

import { generateReport } from 'services/demands';
import { reportsGenerationValidationSchema } from 'yupSchemas/budgeControl';

import { Container, Header, Label } from './styles';
import { openNotification } from 'utils/notification';

interface IFormData {
  periodoInicial: string;
  periodoFinal: string;
}

interface IProps {
  open: boolean;
  onClose(): void;
}

const ReportsGenerationModalComp: React.FC<IProps> = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (data: IFormData) => {
    try {
      setIsLoading(true);
      const [dia, mes, ano] = data.periodoInicial.split('/');

      const [diaf, mesf, anof] = data.periodoFinal.split('/');

      const periodInicial = new Date(`${mes}/${dia}/${ano}`);
      const periodFinal = new Date(`${mesf}/${diaf}/${anof}`);

      if (
        +dia === 0 ||
        +mes === 0 ||
        +ano === 0 ||
        +diaf === 0 ||
        +mesf === 0 ||
        +anof === 0 ||
        periodInicial.toString() === 'Invalid Date' ||
        periodFinal.toString() === 'Invalid Date'
      ) {
        openNotification('Período inválido!', 'warn');
        return;
      }

      if (periodInicial > periodFinal) {
        openNotification('O período inicial não pode ser maior que o período final.', 'warn');
        return;
      }

      if (periodFinal > new Date() || periodInicial > new Date()) {
        openNotification(
          'O período final ou o período inicial não podem ser maiores que a data atual.',
          'warn',
        );
        return;
      }

      const { data: res, headers } = await generateReport({
        periodoInicial: data.periodoInicial,
        periodoFinal: data.periodoFinal,
      });

      const a = document.createElement('a');
      document.body.appendChild(a);
      a.target = '_blank';
      a.href = URL.createObjectURL(res);
      a.download = (headers.get('Content-Disposition') || 'relatorio.xlsx').replace(
        /^.+filename=/g,
        '',
      );
      a.click();
      a.onclick = () => document.body.removeChild(a);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />

      <ModalContent>
        <Container>
          <Header>
            <h1>Exportar relatório</h1>

            <Icon
              name="close"
              marginTop={-0.5}
              marginRight={-0.5}
              clickable
              color={600}
              onClick={onClose}
              size={2}
            />
          </Header>
          <Flex mt="2rem">
            <Unform
              validationSchema={reportsGenerationValidationSchema}
              onSubmit={handleSubmit}
              initialData={{
                formato: 'pdf',
              }}
            >
              <Label>Período</Label>
              <Flex gridGap="16px" mt="10px" mb="21px">
                <MaskInput
                  name="periodoInicial"
                  leftIcon="calendar"
                  mask="date"
                  placeholder="Período inicial..."
                />
                <MaskInput
                  name="periodoFinal"
                  leftIcon="calendar"
                  mask="date"
                  placeholder="Período final..."
                />
              </Flex>
              <Flex mt="1.5rem">
                <Flex ml="auto" gridGap="1rem">
                  <Button
                    type="button"
                    outline
                    variant="cancel"
                    onClick={e => {
                      e.preventDefault();
                      onClose();
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button variant="primary" leftIcon="download" type="submit" isLoading={isLoading}>
                    Baixar
                  </Button>
                </Flex>
              </Flex>
            </Unform>
          </Flex>
        </Container>
      </ModalContent>
    </Modal>
  );
};

export const ReportsGenerationModal = memo(ReportsGenerationModalComp);
